import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { query, collection, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase.js';
import { useAuth } from '../context/AuthContext.js';
import { useLanguage } from '../context/LanguageContext.js';
import { t } from '../utils/translations.js';
import TrackingMap from '../components/TrackingMap.js';
import { formatDuration } from '../utils/formatters.js';
import './TrackingPage.css';

export default function TrackingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [trackings, setTrackings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Carrega dados de rastreamento
  useEffect(() => {
    if (user?.isAdmin) {
      loadTrackings();
    }
  }, [user?.isAdmin]);

  const loadTrackings = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'timeTrackings'),
        orderBy('clockInTime', 'desc')
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTrackings(data);

      // Extrai usuários únicos com count de rastreamentos
      const userMap = {};
      data.forEach(tracking => {
        if (!userMap[tracking.displayName]) {
          userMap[tracking.displayName] = {
            displayName: tracking.displayName,
            userId: tracking.userId,
            count: 0,
            lastTracking: tracking.clockInTime
          };
        }
        userMap[tracking.displayName].count++;
        // Guarda o rastreamento mais recente
        if (tracking.clockInTime > userMap[tracking.displayName].lastTracking) {
          userMap[tracking.displayName].lastTracking = tracking.clockInTime;
        }
      });

      const uniqueUsers = Object.values(userMap).sort((a, b) =>
        b.lastTracking - a.lastTracking
      );
      setUsers(uniqueUsers);
    } catch (err) {
      console.error('Erro ao carregar rastreamentos:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>{t(language, "trackingTitle")}</h1>
          <small className="text-muted">
            {t(language, "trackingSubtitle")}
          </small>
        </Col>
        <Col md="auto">
          <Button
            variant="outline-primary"
            onClick={loadTrackings}
          >
            {t(language, "trackingRefresh")}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t(language, "trackingLoadingData")}</p>
        </div>
      ) : users.length === 0 ? (
        <Alert variant="info">
          {t(language, "trackingNoData")}
        </Alert>
      ) : (
        <>
          {/* Resumo Geral */}
          <Row className="mb-4 g-3">
            <Col xs={6} md={3}>
              <Card className="shadow-sm stat-card">
                <Card.Body className="text-center">
                  <div className="stat-value">{users.length}</div>
                  <div className="stat-label">{t(language, "trackingEmployees")}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="shadow-sm stat-card">
                <Card.Body className="text-center">
                  <div className="stat-value">{trackings.length}</div>
                  <div className="stat-label">{t(language, "trackingTotalTrackings")}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="shadow-sm stat-card">
                <Card.Body className="text-center">
                  <div className="stat-value">
                    {formatDuration(trackings.reduce((sum, t) => sum + (t.totalMinutes || 0), 0))}
                  </div>
                  <div className="stat-label">{t(language, "trackingTotalHours")}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3}>
              <Card className="shadow-sm stat-card">
                <Card.Body className="text-center">
                  <div className="stat-value">
                    {formatDuration(trackings.reduce((sum, t) => sum + (t.totalStationaryMinutes || 0), 0))}
                  </div>
                  <div className="stat-label">{t(language, "trackingStationary")}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Lista de Funcionários em Cards */}
          <Row className="g-4">
            {users.map((person) => (
              <Col lg={4} md={6} key={person.userId} style={{ cursor: 'pointer' }}>
                <Card
                  className="h-100 shadow-sm"
                  onClick={() => navigate(`/admin/tracking/${encodeURIComponent(person.displayName)}`)}
                  style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                >
                  <Card.Body>
                    <h5 className="card-title mb-3">
                      {person.displayName}
                    </h5>

                    <div className="mb-3">
                      <Badge bg="primary" className="me-2">
                        {person.count} {t(language, "trackingTrackings")}
                      </Badge>
                      {person.lastTracking && (
                        <small className="text-muted d-block mt-2">
                          {t(language, "trackingLast")} {person.lastTracking.toDate().toLocaleDateString(language === "pt" ? 'pt-BR' : 'en-NZ')}
                        </small>
                      )}
                    </div>

                    {/* Calcular totais dessa pessoa */}
                    {(() => {
                      const personTrackings = trackings.filter(t => t.displayName === person.displayName);
                      const totalHours = formatDuration(
                        personTrackings.reduce((sum, t) => sum + (t.totalMinutes || 0), 0)
                      );
                      const totalStops = formatDuration(
                        personTrackings.reduce((sum, t) => sum + (t.totalStationaryMinutes || 0), 0)
                      );
                      const totalTravel = formatDuration(
                        personTrackings.reduce((sum, t) => sum + (t.totalTravelMinutes || 0), 0)
                      );

                      return (
                        <div className="stats-mini">
                          <div className="stat-row">
                            <span className="stat-label">{t(language, "trackingTotal")}</span>
                            <span className="stat-value">{totalHours}</span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">{t(language, "trackingStopped")}</span>
                            <span className="stat-value">{totalStops}</span>
                          </div>
                          <div className="stat-row">
                            <span className="stat-label">{t(language, "trackingTravel")}</span>
                            <span className="stat-value">{totalTravel}</span>
                          </div>
                        </div>
                      );
                    })()}

                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3 w-100"
                    >
                      {t(language, "trackingViewDetails")} →
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Container>
  );
}
